import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { FunctionComponent, useEffect, useState } from "react";
import { InfoWarning } from "Infrastructure/Models/Messages/Dto/InfoWarning";
import { LoaderDiv } from "Shared/Icons/LoaderDiv";
import InfoWarningsTable from "./InfoWarningsTable";
import InfoWarningsActions from "./InfoWarningActions";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import {
	InfoWarningCreateMessage,
	InfoWarningDeleteByDescriptionMessage,
} from "Infrastructure/Models/Messages/Messages";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import InstanceResponseContent from "../InstanceResponseContent";
import { getNewFormData as getNewSaveFormData, InfoWarningSaveFormData } from "./InfoWarningActionSave";
import { getNewFormData as getNewDeleteFormData, InfoWarningDeleteFormData } from "./InfoWarningActionDelete";

interface Props {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
}

const InfoWarningsDialogContent: FunctionComponent<Props> = (props) => {
	const { deviceStatuses, deviceStatus, hubConnection, closeDialog } = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [messageResponse, setMessageResponse] = useState<{
		actionName: string;
		requestId?: string | null;
		deviceNames: string[];
	} | null>(null);

	const [saveFormData, setSaveFormData] = useState<InfoWarningSaveFormData>(getNewSaveFormData());
	const [deleteFormData, setDeleteFormData] = useState<InfoWarningDeleteFormData>(getNewDeleteFormData());
	const [infoWarnings, setInfoWarnings] = useState<InfoWarning[] | undefined>(undefined);
	const [isLoadingInfoWarnings, setIsLoadingInfoWarnings] = useState<boolean>(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: fetch once per opened instance
	useEffect(() => {
		if (!deviceStatus) {
			return;
		}
		if (!hubConnection || hubConnection.state !== HubConnectionState.Connected) {
			setInfoWarnings(deviceStatus.infoWarnings);
			return;
		}
		setIsLoadingInfoWarnings(true);
		hubConnection
			.invoke<InfoWarning[]>("getDeviceInfoWarningsRequest", deviceStatus.deviceName)
			.then((result) => setInfoWarnings(result ?? []))
			.catch((error: Error) => {
				logError(error);
				setInfoWarnings(deviceStatus.infoWarnings);
			})
			.finally(() => setIsLoadingInfoWarnings(false));
	}, [deviceStatus?.deviceName]);

	const handleInfoWarningCreate = (
		infoWarningCreateMessage: InfoWarningCreateMessage
	) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		setMessageResponse({
			actionName: "Vytvoření infolišty",
			requestId: infoWarningCreateMessage.requestId,
			deviceNames: infoWarningCreateMessage.filteredDeviceNames,
		});

		hubConnection
			.invoke("infoWarningCreate", infoWarningCreateMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	const handleInfoWarningDelete = (
		infoWarningDeleteMessage: InfoWarningDeleteByDescriptionMessage
	) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		setMessageResponse({
			actionName: "Smazání infolišty",
			requestId: infoWarningDeleteMessage.requestId,
			deviceNames: infoWarningDeleteMessage.filteredDeviceNames,
		});

		hubConnection
			.invoke("infoWarningDeleteByDescription", infoWarningDeleteMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	if (messageResponse) {
		return (
			<InstanceResponseContent
				actionName={messageResponse.actionName}
				requestId={messageResponse.requestId}
				deviceNames={messageResponse.deviceNames}
				hubConnection={hubConnection}
				closeDialog={closeDialog}
			/>
		);
	}

	return (
		<Stack direction="column">
			{deviceStatuses && deviceStatuses?.length > 0 && (
				<InfoWarningsActions
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onCreate={handleInfoWarningCreate}
					onDelete={handleInfoWarningDelete}
					setSaveFormData={setSaveFormData}
					setDeleteFormData={setDeleteFormData}
					saveFormData={saveFormData}
					deleteFormData={deleteFormData}
				/>
			)}
			{deviceStatus && (
				<Box mt={1}>
					<Box my={1}>
						<Typography variant="h6" component="div">
							Data
						</Typography>
						<Divider />
					</Box>
					<Paper>
						{isLoadingInfoWarnings ? (
							<Stack justifyContent="center" alignItems="center" py={4}>
								<LoaderDiv />
							</Stack>
						) : (
							<InfoWarningsTable
								deviceName={deviceStatus?.deviceName}
								infoWarnings={infoWarnings}
								hubConnection={hubConnection}
								closeDialog={closeDialog}
							/>
						)}
					</Paper>
				</Box>
			)}
		</Stack>
	);
};

export default InfoWarningsDialogContent;
